import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Download, User, Calendar, Clock, Target, AlertTriangle, CheckCircle, XCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface TestResult {
  id: string;
  candidate_id: string;
  test_id: string;
  answers: any;
  scores: any;
  personality_profile: any;
  recommendations: string;
  status: string;
  started_at: string;
  completed_at: string;
  completion_time: number;
  candidates: {
    full_name: string;
    email: string;
    phone: string;
  };
  occupational_tests: {
    name: string;
    description: string;
    scoring_criteria: any;
  };
}

export default function TestResultDetailsPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (resultId) {
      fetchTestResult();
    }
  }, [resultId]);

  const fetchTestResult = async () => {
    try {
      const { data, error } = await supabase
        .from('test_results')
        .select(`
          *,
          candidates(full_name, email, phone),
          occupational_tests(name, description, scoring_criteria)
        `)
        .eq('id', resultId)
        .single();

      if (error) throw error;
      setTestResult(data);
    } catch (error) {
      console.error('Error fetching test result:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν μπόρεσαν να φορτωθούν τα αποτελέσματα του τεστ",
        variant: "destructive",
      });
      navigate('/hr');
    } finally {
      setLoading(false);
    }
  };

  const getSuitabilityInfo = (suitability: string) => {
    switch (suitability) {
      case 'suitable':
        return {
          icon: <CheckCircle className="h-5 w-5 text-green-600" />,
          label: 'Κατάλληλος',
          color: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'conditional':
        return {
          icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
          label: 'Υπό προϋποθέσεις',
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'unsuitable':
        return {
          icon: <XCircle className="h-5 w-5 text-red-600" />,
          label: 'Ακατάλληλος',
          color: 'bg-red-100 text-red-800 border-red-200'
        };
      default:
        return {
          icon: <AlertTriangle className="h-5 w-5 text-gray-600" />,
          label: 'Άγνωστο',
          color: 'bg-gray-100 text-gray-800 border-gray-200'
        };
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const handleDownloadReport = () => {
    if (!testResult) return;

    const reportContent = `
=== ΑΝΑΦΟΡΑ ΑΠΟΤΕΛΕΣΜΑΤΩΝ ΤΕΣΤ ===

ΣΤΟΙΧΕΙΑ ΥΠΟΨΗΦΙΟΥ:
Όνομα: ${testResult.candidates.full_name}
Email: ${testResult.candidates.email}
Τηλέφωνο: ${testResult.candidates.phone}

ΣΤΟΙΧΕΙΑ ΤΕΣΤ:
Τεστ: ${testResult.occupational_tests.name}
Ημερομηνία Ολοκλήρωσης: ${new Date(testResult.completed_at).toLocaleString()}
Χρόνος Ολοκλήρωσης: ${testResult.completion_time} λεπτά
Συνολικό Σκορ: ${testResult.scores.overallScore}%

ΚΑΤΑΛΛΗΛΟΤΗΤΑ:
${getSuitabilityInfo(testResult.scores.suitability).label}

ΣΥΣΤΑΣΗ:
${testResult.scores.recommendation}

ΛΕΠΤΟΜΕΡΗ ΑΠΟΤΕΛΕΣΜΑΤΑ:
${testResult.scores.detailedAnalysis}

ΑΠΟΤΕΛΕΣΜΑΤΑ ΑΝΑ ΚΛΙΜΑΚΑ:
${Object.entries(testResult.scores.scaleScores).map(([scale, score]) => 
  `${scale}: ${score}%`
).join('\n')}

---
Αναφορά δημιουργήθηκε στις: ${new Date().toLocaleString()}
    `;

    const blob = new Blob([reportContent], { type: 'text/plain; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${testResult.candidates.full_name}_test_report.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Αναφορά Κατέβηκε",
      description: "Η αναφορά αποτελεσμάτων κατέβηκε επιτυχώς",
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">Φόρτωση αποτελεσμάτων...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!testResult) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">Δεν βρέθηκαν αποτελέσματα</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const suitabilityInfo = getSuitabilityInfo(testResult.scores.suitability);

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/hr')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Επιστροφή
          </Button>
          <h1 className="text-2xl font-bold">Αποτελέσματα Τεστ</h1>
        </div>
        <Button onClick={handleDownloadReport}>
          <Download className="h-4 w-4 mr-2" />
          Κατέβασμα Αναφοράς
        </Button>
      </div>

      {/* Candidate Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Στοιχεία Υποψηφίου</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Όνομα</p>
              <p className="font-medium">{testResult.candidates.full_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{testResult.candidates.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Τηλέφωνο</p>
              <p className="font-medium">{testResult.candidates.phone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Στοιχεία Τεστ</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Τεστ</p>
              <p className="font-medium">{testResult.occupational_tests.name}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Ολοκληρώθηκε</p>
                <p className="font-medium">{new Date(testResult.completed_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Χρόνος</p>
                <p className="font-medium">{testResult.completion_time} λεπτά</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Συνολικό Σκορ</p>
              <p className="font-medium text-lg">{testResult.scores.overallScore}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suitability Assessment */}
      <Card>
        <CardHeader>
          <CardTitle>Αξιολόγηση Καταλληλότητας</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              {suitabilityInfo.icon}
              <Badge className={suitabilityInfo.color}>
                {suitabilityInfo.label}
              </Badge>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">Σύσταση:</h4>
              <p className="text-sm">{testResult.scores.recommendation}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scale Scores */}
      <Card>
        <CardHeader>
          <CardTitle>Αποτελέσματα ανά Κλίμακα</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {testResult.scores.categoryScores && Object.entries(testResult.scores.categoryScores).map(([scale, score]) => {
              const tScore = Number(score);
              const getScaleColor = (score: number) => {
                if (score >= 70) return 'bg-red-500';
                if (score >= 65) return 'bg-yellow-500';
                return 'bg-green-500';
              };
              
              const getScaleLabel = (scale: string) => {
                const labels: Record<string, string> = {
                  'L': 'Κλίμακα Ψεύδους',
                  'F': 'Ασυνήθιστες Απαντήσεις',
                  'K': 'Διόρθωση',
                  'Hs': 'Υποχονδρίαση',
                  'D': 'Κατάθλιψη',
                  'Hy': 'Υστερία',
                  'Pd': 'Ψυχοπαθητική Παρέκκλιση',
                  'Mf': 'Αρρενωπότητα-Θηλυκότητα',
                  'Pa': 'Παράνοια',
                  'Pt': 'Ψυχασθένεια',
                  'Sc': 'Σχιζοφρένεια',
                  'Ma': 'Υπομανία',
                  'Si': 'Κοινωνική Εσωστρέφεια'
                };
                return labels[scale] || scale;
              };

              return (
                <div key={scale} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{getScaleLabel(scale)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">T-Score: {tScore}</span>
                      {tScore >= 70 && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Κλινικά Αυξημένη</span>}
                      {tScore >= 65 && tScore < 70 && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Αυξημένη</span>}
                    </div>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={Math.min(tScore, 100)} 
                      className="h-3"
                    />
                    <div 
                      className={`absolute top-0 left-0 h-3 rounded-sm ${getScaleColor(tScore)}`}
                      style={{ width: `${Math.min(tScore, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Λεπτομερής Ανάλυση</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="whitespace-pre-line text-sm">
              {testResult.scores.clinicalInterpretation || testResult.scores.detailedAnalysis}
            </div>
            
            {testResult.scores.validityScores && (
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Δείκτες Εγκυρότητας:</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">VRIN: </span>
                    <span>{testResult.scores.validityScores.VRIN}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">TRIN: </span>
                    <span>{testResult.scores.validityScores.TRIN}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">F-Back: </span>
                    <span>{testResult.scores.validityScores.F_BACK}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}